import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { NewConnectionsSessionManagerAPI } from 'pages/ClientOverview/pages/ClientConnections/components/NewConnections/constants';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';

import {
    AccessPolicy,
    Address,
    ConnectionsAPI,
    ContactGroup,
    ContactGroupDetails,
    ContactGroupDocuments,
    ContactGroupEnum,
    ContactGroupHistory,
    NewJAConnectionPayload,
    NewProxyConnectionPayload,
    ProxyDetails,
} from '../types';
import { notification } from '../../../ui-library';
import { downloadDmsDocument } from '../../../utils/downloadDmsDocument';
import { useUpdateConnectionMailingAddress } from '../hooks/useUpdateConnectionMailingAddress';

const STALE_TIME = 30 * 60 * 1000;
const GC_TIME = 60 * 60 * 1000;

const ConnectionsContext = createContext<ConnectionsAPI>(null as any);

type ConnectionsManagerProps = {
    // eslint-disable-next-line react/require-default-props
    clientId?: number;
    children: React.ReactNode;
};

export const ConnectionsManager = ({
    clientId,
    children,
}: ConnectionsManagerProps) => {
    const { t } = useTranslation();

    const [error, setError] = useState<string | null>(null);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<ContactGroup[]>([]);
    const [rawData, setRawData] = useState<ContactGroup[]>([]);
    const [contactGroupDocuments, setContactGroupDocuments] = useState<Array<ContactGroupDocuments> | null>(null);
    const [selectedProxyConnection, setSelectedProxyConnection] = useState<ProxyDetails | null>(null);
    const [isAddConnectionModalOpen, setIsAddConnectionModalOpen] = useState<boolean>(false);
    const [contactGroupDetails, setContactGroupDetails] = useState<ContactGroupDetails | null>(null);
    const [contactGroupHistory, setContactGroupHistory] = useState<ContactGroupHistory | null>(null);
    const [downloadDocumentInProgress, setDownloadDocumentInProgress] = useState<{
        id: number;
        inProgress: boolean;
    } | null>(null);
    const [allowProxyAddition, setAllowProxyAddition] = useState<boolean>(true);
    const [addresses, setAddress] = useState<Address[]>([]);

    // Query trigger states — setting these enables the corresponding query
    const [historyGroupId, setHistoryGroupId] = useState<number | null>(null);
    const [detailsGroupId, setDetailsGroupId] = useState<number | null>(null);
    const [documentsGroupId, setDocumentsGroupId] = useState<number | null>(null);
    const [downloadDocId, setDownloadDocId] = useState<number | null>(null);
    const [proxyStatusGroupId, setProxyStatusGroupId] = useState<string | null>(null);
    const [addressContacts, setAddressContacts] = useState<number[] | null>(null);

    const onAddConnection = (): void => { setIsAddConnectionModalOpen(true); };

    const { inProgress, updateContactGroupAddress } = useUpdateConnectionMailingAddress();

    // ─── Queries ────────────────────────────────────────────────────────────────

    const { data: contactGroupsResponse, isLoading: isContactGroupsLoading } = useGetCustomService({
        service: 'customClientService',
        api: 'getContactGroups',
        payload: { contactId: clientId, createThreeB: false, createMortgage: false },
        enabled: !!clientId,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        refetchOnWindowFocus: false,
    });

    const {
        data: historyResponse,
        isLoading: contactGroupHistoryLoading,
        error: contactGroupHistoryError,
    } = useGetCustomService({
        service: 'customClientService',
        api: 'getContactGroupHistory',
        payload: historyGroupId,
        enabled: !!historyGroupId,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        refetchOnWindowFocus: false,
    });

    const { data: detailsResponse, isLoading: isDetailsLoading } = useGetCustomService({
        service: 'customClientService',
        api: 'getContactGroupDetails',
        payload: { groupId: detailsGroupId },
        enabled: !!detailsGroupId,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        refetchOnWindowFocus: false,
    });

    const {
        data: documentsResponse,
        isLoading: contactGroupDocumentsInProgress,
        error: contactGroupDocumentsError,
    } = useGetCustomService({
        service: 'customDocumentsService',
        api: 'getContactGroupDocuments',
        payload: { contactGroupId: documentsGroupId },
        enabled: !!documentsGroupId,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
    });

    const { data: downloadDocResponse } = useGetCustomService({
        service: 'documents',
        api: 'getDocumentById',
        payload: downloadDocId,
        enabled: !!downloadDocId,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
    });

    const {
        data: proxyStatusResponse,
        isLoading: isProxyStatusLoading,
    } = useGetCustomService({
        service: 'customClientService',
        api: 'getProxyAdditionStatus',
        payload: proxyStatusGroupId ? parseInt(proxyStatusGroupId, 10) : null,
        enabled: !!proxyStatusGroupId,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
    });

    const {
        data: addressesResponse,
        isLoading: isAddressesQueryLoading,
        error: addressError,
    } = useGetCustomService({
        service: 'customClientService',
        api: 'getContactGroupAddresses',
        payload: { contacts: addressContacts },
        enabled: !!addressContacts?.length,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
    });

    // ─── Mutations ────────────────────────────────────────────────────────────────
    const {
        mutateAsync: handleConnectionMutation,
    } = useGenericAPIMutation();

    // ─── Effects — sync query data to local state ────────────────────────────────

    useEffect(() => {
        if (!isContactGroupsLoading && contactGroupsResponse?.data) {
            const connectionsData = contactGroupsResponse.data as ContactGroup[];

            setData(
                connectionsData.filter(
                    (f) => !!f.proxies?.length || f.groupName !== ContactGroupEnum.threeB,
                ),
            );
            setRawData(connectionsData);
        }
    }, [contactGroupsResponse, isContactGroupsLoading]);

    useEffect(() => {
        if (historyResponse?.data) {
            setContactGroupHistory(historyResponse.data as ContactGroupHistory);
        }
    }, [historyResponse]);

    useEffect(() => {
        if (detailsResponse?.data) {
            setContactGroupDetails(detailsResponse.data as ContactGroupDetails);
        }
    }, [detailsResponse]);

    // downloadDocument is defined below but referenced here via closure — stable useCallback, safe
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const downloadDocument = useCallback((documentId: number): void => {
        setDownloadDocumentInProgress({ id: documentId, inProgress: true });
        setDownloadDocId(documentId);
    }, []);

    useEffect(() => {
        if (documentsResponse?.data) {
            const documents = (documentsResponse.data as Array<ContactGroupDocuments>).map((item) => ({
                ...item,
                nameCol: {
                    link: '',
                    value: item.id,
                    onDownload: () => downloadDocument(item.id),
                },
            }));

            setContactGroupDocuments(documents);
        }
    }, [documentsResponse, downloadDocument]);

    useEffect(() => {
        setContactGroupDocuments(
            (prev) => prev?.map((item) => ({
                ...item,
                nameCol: {
                    ...item.nameCol,
                    inProgress:
                        item.id === downloadDocumentInProgress?.id
                            ? downloadDocumentInProgress.inProgress
                            : false,
                },
            })) || null,
        );
    }, [downloadDocumentInProgress]);

    useEffect(() => {
        if (downloadDocId && downloadDocResponse?.data) {
            try {
                const { newBlob } = downloadDmsDocument(downloadDocResponse.data, {
                    saveFile: true,
                    name: undefined,
                });
                const url = URL.createObjectURL(newBlob);

                window.open(url, '_blank', 'noopener,noreferrer');
            } catch (err: any) {
                notification.open({
                    content: `${t('contactGroups.somethingWentWrong')} ${err.message}`,
                    type: 'error',
                });
            } finally {
                setDownloadDocumentInProgress({ id: downloadDocId, inProgress: false });
                // Reset to allow re-download of the same document
                setDownloadDocId(null);
            }
        }
    }, [downloadDocResponse, downloadDocId, t]);

    useEffect(() => {
        if (proxyStatusResponse?.data) {
            setAllowProxyAddition((proxyStatusResponse.data as any)?.canAddProxy);
        } else if (proxyStatusResponse === null && proxyStatusGroupId) {
            setAllowProxyAddition(false);
        }
    }, [proxyStatusResponse, proxyStatusGroupId]);

    useEffect(() => {
        if (addressesResponse?.data) {
            setAddress(addressesResponse.data as Address[]);
        }
    }, [addressesResponse]);

    // ─── Exposed trigger functions ───────────────────────────────────────────────

    const getContactGroupHistory = useCallback((groupId?: number): void => {
        if (groupId) setHistoryGroupId(groupId);
    }, []);

    const getContactGroupDetails = useCallback((groupId?: number): void => {
        if (groupId) setDetailsGroupId(groupId);
    }, []);

    const getContactGroupDocuments = useCallback((contactGroupId?: number): void => {
        if (contactGroupId) setDocumentsGroupId(contactGroupId);
    }, []);

    const getProxyAdditionStatus = useCallback((contactGroupId?: string | null): void => {
        if (contactGroupId) setProxyStatusGroupId(contactGroupId);
    }, []);

    const loadContactAddresses = useCallback((contacts: number[]): void => {
        if (contacts?.length) setAddressContacts(contacts);
    }, []);

    // ─── Mutation handlers ───────────────────────────────────────────────────────

    const onDeleteProxyConnection = (): void => {
        setContactGroupDetails((prev) => (prev
            ? {
                ...prev,
                proxies: prev.proxies.filter(
                    (proxy: ProxyDetails) => proxy.proxyId !== selectedProxyConnection?.proxyId,
                ),
            }
            : null));
        setSelectedProxyConnection(null);
    };

    const onProxyConnectionDelete = useCallback(async (): Promise<void> => {
        if (
            !selectedProxyConnection
            || ![
                AccessPolicy.Representative,
                AccessPolicy.PowerOfAttorney,
            ].includes(selectedProxyConnection.accessPolicy)
        ) {
            return;
        }

        try {
            setLoading(true);
            setError(null);
            await handleConnectionMutation({
                service: 'customClientService',
                API: 'deleteProxyConnection',
                payload: {
                    contactGroupId: contactGroupDetails?.groupId,
                    proxyId: selectedProxyConnection.proxyId,
                    accessPolicy: selectedProxyConnection.accessPolicy,
                },
                invalidateQueryKeys: [
                    'getData_getContactGroups',
                    'getData_getContactGroupDetails',
                    'getData_getContactGroupHistory',
                ],
            });
            onDeleteProxyConnection();
            notification.open({
                content: t('contactGroups.proxyDeletedSuccessfully'),
                type: 'success',
            });
        } catch {
            // error notification handled by useGenericAPIMutation onError
        } finally {
            setLoading(false);
        }
    }, [selectedProxyConnection, contactGroupDetails, handleConnectionMutation, t]);

    const deleteContactGroup = useCallback(
        async (contactGroupId: number) => {
            try {
                await handleConnectionMutation({
                    service: 'customClientService',
                    API: 'deleteContactGroup',
                    payload: { contactGroupId },
                    invalidateQueryKeys: ['getData_getContactGroups', 'getData_getContactGroupHistory'],
                });
                // Optimistic local update while query refetches
                setData((prev) => prev.filter((f) => f.groupId !== contactGroupId));
                setRawData((prev) => prev.filter((f) => f.groupId !== contactGroupId));
                notification.open({
                    content: t('contactGroups.contactGroupDeletedSuccessfully'),
                    type: 'success',
                });
            } catch {
                // error notification handled by useGenericAPIMutation onError
            }
        },
        [t, handleConnectionMutation],
    );

    const createConnection = useCallback(
        async (context: NewConnectionsSessionManagerAPI): Promise<void> => {
            const {
                contactId,
                clients,
                accessPolicy,
                relationship,
                mailingAddress,
                documents,
                includeCCDoc,
            } = context;

            try {
                setLoading(true);
                setError(null);
                if (
                    context.connectionType === 'poa'
                    || context.connectionType === 'representative'
                ) {
                    if (
                        !clients?.length
                        || !accessPolicy
                        || !documents?.length
                    ) {
                        throw new Error(t('contactGroups.dataMissing'));
                    }

                    const payload: NewProxyConnectionPayload = {
                        proxyId: clients[0].id,
                        accessPolicy,
                        proxyRelationship: {
                            description: relationship || '',
                            ccApproval: includeCCDoc ?? false,
                        },
                        documents: documents.map((doc) => ({
                            file: doc.file,
                            filename: doc.filename,
                            type: doc.type,
                        })),
                    };

                    if (context.contactGroupId && context.addForJA) {
                        await handleConnectionMutation({
                            service: 'customClientService',
                            API: 'updateContactGroupProxy',
                            payload: {
                                contactGroupId: context.contactGroupId, ...payload,
                            },
                            invalidateQueryKeys: ['getData_getContactGroups', 'getData_getContactGroupDetails', 'getData_getContactGroupHistory'],
                        });
                    } else {
                        await handleConnectionMutation({
                            service: 'customClientService',
                            API: 'createProxyConnection',
                            payload: {
                                contactId, ...payload,
                            },
                            invalidateQueryKeys: ['getData_getContactGroups', 'getData_getContactGroupDetails', 'getData_getContactGroupHistory'],
                        });
                    }
                    notification.open({
                        content: t('contactGroups.proxyAddedSuccessfully'),
                        type: 'success',
                    });
                } else {
                    if (!clients?.length || !mailingAddress || !contactId) {
                        throw new Error(t('contactGroups.dataMissing'));
                    }

                    const payload: NewJAConnectionPayload = {
                        contacts: [...clients.map((c) => c.id), contactId],
                        mailingAddress,
                    };

                    await handleConnectionMutation({
                        service: 'customClientService',
                        API: 'createJAConnection',
                        payload: {
                            contactId, ...payload,
                        },
                        invalidateQueryKeys: ['getData_getContactGroups'],
                    });
                    notification.open({
                        content: t(
                            'contactGroups.jointAccountAddedSuccessfully',
                        ),
                        type: 'success',
                    });
                }
                // Cache invalidation handled by individual mutation onSuccess callbacks
            } catch (err: any) {
                notification.open({
                    content: `${t('contactGroups.somethingWentWrong')} ${
                        err.message
                    }`,
                    type: 'error',
                });
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [t, handleConnectionMutation],
    );

    const exposedAPI = useMemo(
        () => ({
            data,
            isLoading: isLoading || isContactGroupsLoading || isDetailsLoading,
            error,
            onAddConnection,
            selectedProxyConnection,
            discardConnectionRemove: () => setSelectedProxyConnection(null),
            isAddConnectionModalOpen,
            closeAddConnectionModal: () => setIsAddConnectionModalOpen(false),
            createConnection,
            getContactGroupDetails,
            contactGroupDetails,
            setSelectedProxyConnection,
            onProxyConnectionDelete,
            deleteContactGroup,
            getContactGroupDocuments,
            contactGroupDocumentsInProgress,
            contactGroupDocumentsError: contactGroupDocumentsError?.message ?? null,
            contactGroupDocuments,
            addresses,
            isAddressesLoading: isAddressesQueryLoading || inProgress,
            inProgress,
            loadContactAddresses,
            addressError: addressError?.message ?? null,
            getContactGroupHistory,
            contactGroupHistoryLoading,
            contactGroupHistoryError: contactGroupHistoryError?.message ?? null,
            contactGroupHistory,
            setContactGroupDetails,
            updateContactGroupAddress,
            downloadDocument,
            getProxyAdditionStatus,
            allowProxyAddition,
            rawData,
            isProxyStatusLoading,
        }),
        [
            data,
            isLoading,
            isContactGroupsLoading,
            isDetailsLoading,
            error,
            selectedProxyConnection,
            isAddConnectionModalOpen,
            createConnection,
            getContactGroupDetails,
            contactGroupDetails,
            contactGroupDocuments,
            getContactGroupDocuments,
            contactGroupDocumentsInProgress,
            contactGroupDocumentsError,
            onProxyConnectionDelete,
            addresses,
            isAddressesQueryLoading,
            inProgress,
            loadContactAddresses,
            addressError,
            getContactGroupHistory,
            contactGroupHistoryLoading,
            contactGroupHistoryError,
            contactGroupHistory,
            updateContactGroupAddress,
            downloadDocument,
            getProxyAdditionStatus,
            allowProxyAddition,
            rawData,
            isProxyStatusLoading,
        ],
    );

    return (
        <ConnectionsContext.Provider value={exposedAPI}>
            {children}
        </ConnectionsContext.Provider>
    );
};

export const useConnections = (): ConnectionsAPI => useContext(ConnectionsContext);
